// Week arithmetic for the progress views. Step-state dates arrive as ISO
// calendar dates ("YYYY-MM-DD") already rounded to the Monday of their week on
// the device; everything here works on those strings in UTC so a date never
// shifts by a day with the viewer's time zone.

import type { IsoDate } from "../domain/types";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Parse "YYYY-MM-DD" (or a full ISO timestamp) as a UTC midnight. */
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
}

/** Format a Date as "YYYY-MM-DD" using its UTC calendar day. */
export function toIsoDate(date: Date): IsoDate {
  return date.toISOString().slice(0, 10);
}

/** The viewer's LOCAL calendar day of an instant, as "YYYY-MM-DD". */
export function localDay(at: Date | string): IsoDate {
  const d = typeof at === "string" ? new Date(at) : at;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** The Monday on or before the given calendar day. */
export function mondayOf(iso: IsoDate): IsoDate {
  const d = parseIsoDate(iso);
  const back = (d.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  return toIsoDate(new Date(d.getTime() - back * DAY_MS));
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  return toIsoDate(new Date(parseIsoDate(iso).getTime() + days * DAY_MS));
}

export function addWeeks(iso: IsoDate, weeks: number): IsoDate {
  return addDays(iso, weeks * 7);
}

/** Whole weeks from `a` to `b` (b later → positive), on Monday-rounded dates. */
export function weeksBetween(a: IsoDate, b: IsoDate): number {
  return Math.round(
    (parseIsoDate(mondayOf(b)).getTime() - parseIsoDate(mondayOf(a)).getTime()) / (7 * DAY_MS),
  );
}

/** Whole days from `a` to `b`. */
export function daysBetween(a: IsoDate, b: IsoDate): number {
  return Math.round((parseIsoDate(b).getTime() - parseIsoDate(a).getTime()) / DAY_MS);
}
